import React, { useEffect } from 'react';
import { Modal, ModalFooter, ModalMedia } from '@lux/modal';
import { Celebration, GraphicSize } from '@lux/graphics';
import { PlusTierPill, Size } from '@lux/plus-badge';
import { Translate, useFormatters } from '@pegasus/translation';
import { ComplementaryColor, FunctionColor, screenSizes } from '@lux/core';
import { useSelector } from 'react-redux';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { trackSelectContent, trackViewContent } from '@browsing/tracking';
import { TitlePreset } from '@lux/title';
import { useLoyalty } from '@client/components/mylounge/hooks/use-loyalty';
import { FreeDeliveryTermsConditionsDialog } from '@client/components/mylounge/plus/benefits/free-delivery/terms-conditions-dialog';
import { useUnlimitedFreeDelivery } from '@client/components/mylounge/plus/use-unlimited-free-delivery';
import { Preset, Text } from '@lux/text';
import {
    ModalDescription,
    ModalOverlay,
    ModalTitle,
    ModalWrapper,
    PlusBadgeWrapper,
} from './styles';

interface MemberFreeDeliveryDialogProps {
    open: boolean;
    onClose: () => void;
    minimumOrderValue: number;
}

export const MemberFreeDeliveryDialog: React.FC<MemberFreeDeliveryDialogProps> = ({
    open,
    onClose,
    minimumOrderValue,
}) => {
    const deviceWidth = useSelector(getClientDeviceWidth);
    const isMobileView = deviceWidth < screenSizes.sm;
    const loyalty = useLoyalty();
    const tier = loyalty.summary.tier;
    const { formatPrice } = useFormatters();
    const { isActive: isUnlimitedFreeDeliveryActive, month } = useUnlimitedFreeDelivery();

    useEffect(() => {
        if (open) {
            trackViewContent('loyalty_free_delivery');
        }
    }, [open]);

    const [openTCModal, setOpenTCModal] = React.useState(false);

    return (
        <>
            <Modal
                dismissable
                open={open && !openTCModal}
                onOpenChange={onClose}
                purpose="NON_COMMERCIAL"
                role="dialog"
                size="S"
                sizeSm="S"
                mobileFullScreen={isMobileView}
            >
                <ModalOverlay
                    dismissLabel="Close dialog"
                    withoutCloseIcon
                    onClick={() => {
                        trackSelectContent('loyalty_free_delivery', 'click', {
                            info: 'click',
                            contentId: 'close',
                        });
                        onClose();
                    }}
                >
                    <ModalWrapper onClick={e => e.stopPropagation()} isMember={true}>
                        <PlusBadgeWrapper>
                            <PlusTierPill tier={tier} size={Size.M} dynamicFontSize>
                                <Translate message={`plus.loyalty.tier.${tier}`} />
                            </PlusTierPill>
                        </PlusBadgeWrapper>

                        <ModalMedia>
                            <Celebration
                                size={GraphicSize.L}
                                bgColor={ComplementaryColor.brand30}
                            />
                        </ModalMedia>

                        <ModalTitle preset={TitlePreset.M}>
                            <Translate
                                message={
                                    isUnlimitedFreeDeliveryActive
                                        ? 'free_delivery.unlimited.member.popup.title'
                                        : 'free_delivery.member.popup.title'
                                }
                                params={{
                                    minimumOrderValue: formatPrice({
                                        cents: minimumOrderValue * 100,
                                        maximumFractionDigits: 0,
                                    }),
                                }}
                            />
                        </ModalTitle>

                        <ModalDescription>
                            <Translate
                                message={
                                    isUnlimitedFreeDeliveryActive
                                        ? 'free_delivery.unlimited.member.popup.description'
                                        : 'free_delivery.member.popup.description'
                                }
                                params={{
                                    minimumOrderValue: formatPrice({
                                        cents: minimumOrderValue * 100,
                                        maximumFractionDigits: 0,
                                    }),
                                    month,
                                }}
                            />
                        </ModalDescription>

                        <ModalFooter
                            primaryAction={{
                                children: <Translate message="free_delivery.member.popup.shop" />,
                                href: '#',
                                link: true,
                                onClick: () => {
                                    trackSelectContent('loyalty_free_delivery', 'click', {
                                        info: 'click',
                                        contentId: 'shop',
                                    });
                                    onClose();
                                },
                            }}
                        >
                            {isUnlimitedFreeDeliveryActive && (
                                <div className="mb-m sm:mb-0">
                                    <Text
                                        as="button"
                                        preset={Preset.XS}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setOpenTCModal(true);
                                        }}
                                        color={FunctionColor.function80}
                                    >
                                        <Translate
                                            message="free_delivery.unlimited.member.popup.tnc.link"
                                            html
                                            raw
                                        />
                                    </Text>
                                </div>
                            )}
                        </ModalFooter>
                    </ModalWrapper>
                </ModalOverlay>
            </Modal>
            <FreeDeliveryTermsConditionsDialog
                open={openTCModal}
                onBack={() => setOpenTCModal(false)}
            />
        </>
    );
};
